<template>
  <component
    :is="htmlTag"
    class="main-content"
    :class="{
      'has-disabled-padding': disablePadding,
      'has-disabled-background': disableBackground,
    }"
  >
    <component
      :is="useComponent(item.component)"
      v-for="item of data"
      v-bind="buildProps(item)"
      :key="item._uid"
      v-editable="item"
      :class="[rtl ? 'is-rtl-section' : '']"
    />
  </component>
</template>

<script setup>
defineProps({
    data: {
        type: Array,
        default: () => [],
    },

    htmlTag: {
        type: String,
        default: 'main',
    },

    rtl: {
        type: Boolean,
        default: false,
    },

    disablePadding: {
        type: Boolean,
        default: false,
    },

    disableBackground: {
        type: Boolean,
        default: false,
    },
});

const buildProps = (data) => {
    let itemProps = { ...data };

    if (data.component === 'StoryblokFormContact') {
        if (itemProps.useGlobalConfiguration) {
            const globalSettings = getStoryblokSetting('globalcomponents', 'contactForm');

            if (isArrayNotEmpty(globalSettings?.value)) {
                const config = globalSettings.value[0];

                Object.entries(itemProps).forEach(([key, value]) => {
                    if (isString(value) && value === '') {
                        delete itemProps[key];
                    }

                    if (isArray(value) && isArrayEmpty(value)) {
                        delete itemProps[key];
                    }

                    if (isBoolean(value) && value === false) {
                        delete itemProps[key];
                    }
                });

                itemProps = {
                    ...config,
                    ...itemProps,
                };
            }
        }
    }

    delete itemProps.component;

    return itemProps;
};

/*
  Add more components here if you need them in the storybook render loop
*/
const availableComponents = {
    StoryblokTextHeading: resolveComponent('StoryblokTextHeading'),
    StoryblokImage: resolveComponent('StoryblokImage'),
    StoryblokTextRichtext: resolveComponent('StoryblokTextRichtext'),
    StoryblokIntroText: resolveComponent('StoryblokIntroText'),
    StoryblokHero: resolveComponent('StoryblokHero'),
    StoryblokHeroLinks: resolveComponent('StoryblokHeroLinks'),
    StoryblokLink: resolveComponent('StoryblokLink'),
    StoryblokVideo: resolveComponent('StoryblokVideo'),
    StoryblokVideoYoutube: resolveComponent('StoryblokVideoYoutube'),
    StoryblokQuote: resolveComponent('StoryblokQuote'),
    StoryblokAccordion: resolveComponent('StoryblokAccordion'),
    StoryblokTeaserCard: resolveComponent('StoryblokTeaserCard'),
    StoryblokIframe: resolveComponent('StoryblokIframe'),
    StoryblokProfile: resolveComponent('StoryblokProfile'),
    StoryblokInfoBox: resolveComponent('StoryblokInfoBox'),
    StoryblokImageGallery: resolveComponent('StoryblokImageGallery'),
    StoryblokSliderHero: resolveComponent('StoryblokSliderHero'),
    StoryblokTeaserPdf: resolveComponent('StoryblokTeaserPdf'),
    StoryblokKeyFigures: resolveComponent('StoryblokKeyFigures'),
    StoryblokTeaser: resolveComponent('StoryblokTeaser'),
    StoryblokTeaserCurated: resolveComponent('StoryblokTeaserCurated'),
    StoryblokOverlay: resolveComponent('StoryblokOverlay'),
    StoryblokSliderEnumeration: resolveComponent('StoryblokSliderEnumeration'),
    StoryblokNavigationSide: resolveComponent('StoryblokNavigationSide'),
    StoryblokTeaserDepartments: resolveComponent('StoryblokTeaserDepartments'),
    StoryblokTeaserNewsRegional: resolveComponent('StoryblokTeaserNewsRegional'),
    StoryblokTeaserNewsInternational: resolveComponent('StoryblokTeaserNewsInternational'),
    StoryblokTeaserBox: resolveComponent('StoryblokTeaserBox'),
    StoryblokFormContact: resolveComponent('StoryblokFormContact'),
    StoryblokInputCheckbox: resolveComponent('StoryblokInputCheckbox'),
    StoryblokInputMultiselect: resolveComponent('StoryblokInputMultiselect'),
    StoryblokInputRadio: resolveComponent('StoryblokInputRadio'),
    StoryblokInputText: resolveComponent('StoryblokInputText'),
    StoryblokInputTextarea: resolveComponent('StoryblokInputTextarea'),
    StoryblokTeaserProgramm: resolveComponent('StoryblokTeaserProgramm'),
    StoryblokSliderParticipation: resolveComponent('StoryblokSliderParticipation'),
    StoryblokKeyFiguresInternational: resolveComponent('StoryblokKeyFiguresInternational'),
    StoryblokTeaserSublinks: resolveComponent('StoryblokTeaserSublinks'),
    StoryblokTeaserSublinksScalable: resolveComponent('StoryblokTeaserSublinks'),
    StoryblokInfoProgramm: resolveComponent('StoryblokInfoProgramm'),
    StoryblokCompass: resolveComponent('StoryblokCompass'),
    StoryblokTagCloud: resolveComponent('StoryblokTagCloud'),
    StoryblokFilter: resolveComponent('StoryblokFilter'),
    StoryblokNewsFiltered: resolveComponent('StoryblokNewsFiltered'),
    StoryblokEmployeesBlock: resolveComponent('StoryblokEmployeesBlock'),
    StoryblokActivitiesFiltered: resolveComponent('StoryblokActivitiesFiltered'),
    BlockParticipationProcessSite: resolveComponent('BlockParticipationProcessSite'),
    StoryblokMapInternational: resolveComponent('StoryblokMapInternational'),
    StoryblokDownloadHub: resolveComponent('StoryblokDownloadHub'),
    StoryblokDownloadHubSmall: resolveComponent('StoryblokDownloadHubSmall'),
    StoryblokDownloadHubMini: resolveComponent('StoryblokDownloadHubMini'),
    StoryblokTeaserProjects: resolveComponent('StoryblokTeaserProjects'),
    StoryblokProjectsBlock: resolveComponent('StoryblokProjectsBlock'),
    StoryblokContributionCalculator: resolveComponent('StoryblokContributionCalculator'),
    StoryblokNewsletter: resolveComponent('StoryblokNewsletter'),
    StoryblokBreadcrumbs: resolveComponent('StoryblokBreadcrumbs'),
    StoryblokColorPalette: resolveComponent('StoryblokColorPalette'),
    StoryblokCustomCode: resolveComponent('StoryblokCustomCode'),
    BlockImplementationWheel: resolveComponent('BlockImplementationWheel'),
    UtilSection: resolveComponent('UtilSection'),
    BlockSectionIntro: resolveComponent('BlockSectionIntro'),
    BlockStickyTitleSection: resolveComponent('BlockStickyTitleSection'),
    BlockTeaserArticle: resolveComponent('BlockTeaserArticle'),
    StoryblokTextLead: resolveComponent('StoryblokTextLead'),
    StoryblokTextBox: resolveComponent('StoryblokTextBox'),
    BlockTextImage: resolveComponent('BlockTextImage'),
    BlockLinkBox: resolveComponent('BlockLinkBox'),
    BlockFacts: resolveComponent('BlockFacts'),
    AtomButtonBack: resolveComponent('AtomButtonBack'),
    StoryblokFormRegistration: resolveComponent('StoryblokFormRegistration'),
    StoryblokTeaserDepartmentsScroll: resolveComponent('StoryblokTeaserDepartmentsScroll'),
    StoryblokTeaserNewsRegionalScroll: resolveComponent('StoryblokTeaserNewsRegionalScroll'),
    StoryblokTeaserNewsInternationalScroll: resolveComponent('StoryblokTeaserNewsInternationalScroll'),
    BlockCounterScrollBased: resolveComponent('BlockCounterScrollBased'),
    StoryblokScrollytellingHero: resolveComponent('StoryblokScrollytellingHero'),
    StoryblokTeaserNewsScroll: resolveComponent('StoryblokTeaserNewsScroll'),
    StoryblokTeaserDepartmentsScrollHorizontal: resolveComponent('StoryblokTeaserDepartmentsScrollHorizontal'),

    // News 2025-08-20
    StoryblokNews: resolveComponent('StoryblokNews'),
    StoryblokTeaserNews: resolveComponent('StoryblokTeaserNews'),
};

const useComponent = (key) => availableComponents[key] || null;

</script>
